import React, { useState, useCallback, useEffect } from "react";
import { Form } from "react-bootstrap";
import {
  Plus, Pencil, Trash2, TestTube2, ChevronRight, Play, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import * as plmSvc from "../../services/plmService";
import EntityFormDialog from "./EntityFormDialog";
import TagInput from "./TagInput";

const EXEC_STATUSES = ["Passed", "Failed", "Blocked", "Skipped"];

const EMPTY_TC = { title: "", description: "", preconditions: "", expectedResult: "", requirementId: "", tags: [] };
const EMPTY_EXEC = { status: "Passed", actualResult: "", errorLog: "", appVersion: "" };

const TestCasesTab = ({ token, plm }) => {
  const { testCases, requirements, tags: allTags, createTestCase, updateTestCase, deleteTestCase, fetchTestCases } = plm;
  const tagNames = allTags.map((t) => t.name);

  const [showTcDlg, setShowTcDlg] = useState(false);
  const [editingTc, setEditingTc] = useState(null);
  const [tcForm, setTcForm] = useState(EMPTY_TC);

  const [showExecDlg, setShowExecDlg] = useState(false);
  const [execForm, setExecForm] = useState(EMPTY_EXEC);
  const [execTcId, setExecTcId] = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const [tc, execs] = await Promise.all([
        plmSvc.fetchTestCaseByIdApi({ token, testCaseId: id }),
        plmSvc.fetchExecutionsApi({ token, testCaseId: id }),
      ]);
      setDetail(tc);
      setExecutions(execs);
    } catch { setDetail(null); setExecutions([]); }
    finally { setLoadingDetail(false); }
  }, [token]);

  useEffect(() => {
    if (expandedId) loadDetail(expandedId);
  }, [expandedId, loadDetail]);

  const openCreate = () => { setEditingTc(null); setTcForm(EMPTY_TC); setShowTcDlg(true); };
  const openEdit = (tc) => {
    setEditingTc(tc);
    setTcForm({
      title: tc.title, description: tc.description || "",
      preconditions: tc.preconditions || "", expectedResult: tc.expectedResult || "",
      requirementId: tc.requirementId || "", tags: tc.tags || [],
    });
    setShowTcDlg(true);
  };

  const handleTcSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...tcForm };
    if (!body.requirementId) delete body.requirementId;
    if (editingTc) {
      await updateTestCase(editingTc.id, body);
    } else {
      await createTestCase(body);
    }
    setSaving(false);
    setShowTcDlg(false);
    if (expandedId) loadDetail(expandedId);
  };

  const handleDeleteTc = async (id) => {
    if (!window.confirm("Delete this test case and all its executions?")) return;
    await deleteTestCase(id);
    if (expandedId === id) { setExpandedId(null); setDetail(null); }
  };

  const openExec = (tcId) => { setExecTcId(tcId); setExecForm(EMPTY_EXEC); setShowExecDlg(true); };

  const handleExecSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await plmSvc.createExecutionApi({ token, testCaseId: execTcId, execution: execForm });
    await loadDetail(execTcId);
    await fetchTestCases();
    setSaving(false);
    setShowExecDlg(false);
  };

  const setTc = (k, v) => setTcForm({ ...tcForm, [k]: v });
  const setExec = (k, v) => setExecForm({ ...execForm, [k]: v });

  const execBadgeColor = (status) => {
    switch (status) { case "Passed": return "green"; case "Failed": return "red"; default: return "yellow"; }
  };

  const execDotClass = (status) => {
    switch (status) { case "Passed": return "passed"; case "Failed": return "failed"; default: return "other"; }
  };

  // Stats
  const totalTc = testCases.length;
  const passedTc = testCases.filter(tc => tc.latestExecution?.status === "Passed").length;
  const failedTc = testCases.filter(tc => tc.latestExecution?.status === "Failed").length;

  return (
    <div>
      <div className="plm-section-header">
        <h2 className="plm-section-title">
          <span className="icon-wrap"><TestTube2 size={20} /></span>
          Test Cases
        </h2>
        <button className="plm-btn-primary" onClick={openCreate}>
          <Plus size={16} /> New Test Case
        </button>
      </div>

      {/* Quick stats */}
      {totalTc > 0 && (
        <div className="plm-stats">
          <div className="plm-stat-card">
            <div className="plm-stat-value">{totalTc}</div>
            <div className="plm-stat-label">Total</div>
          </div>
          <div className="plm-stat-card">
            <div className="plm-stat-value" style={{ color: "var(--plm-success)" }}>{passedTc}</div>
            <div className="plm-stat-label">Passed</div>
          </div>
          <div className="plm-stat-card">
            <div className="plm-stat-value" style={{ color: "var(--plm-danger)" }}>{failedTc}</div>
            <div className="plm-stat-label">Failed</div>
          </div>
        </div>
      )}

      {testCases.length === 0 ? (
        <div className="plm-empty">
          <div className="plm-empty-icon"><TestTube2 size={28} /></div>
          <div className="plm-empty-title">No test cases yet</div>
          <div className="plm-empty-desc">Create test cases to validate your requirements.</div>
          <button className="plm-btn-primary" onClick={openCreate}>
            <Plus size={16} /> Create Test Case
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {testCases.map((tc) => (
            <div key={tc.id} className="plm-card">
              <div
                className="plm-card-header"
                onClick={() => setExpandedId(expandedId === tc.id ? null : tc.id)}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <ChevronRight size={16} className={`plm-chevron ${expandedId === tc.id ? "open" : ""}`} />
                  <span style={{ fontWeight: 700 }}>{tc.title}</span>
                  {tc.latestExecution && (
                    <span className={`plm-badge ${execBadgeColor(tc.latestExecution.status)}`}>
                      {tc.latestExecution.status}
                    </span>
                  )}
                  {tc.requirementTitle && (
                    <span style={{ fontSize: "0.78rem", color: "var(--plm-text-muted)" }}>({tc.requirementTitle})</span>
                  )}
                </div>
                <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button className="plm-btn-ghost success" title="Record Execution" onClick={() => openExec(tc.id)}>
                    <Play size={14} />
                  </button>
                  <button className="plm-btn-ghost" onClick={() => openEdit(tc)}><Pencil size={14} /></button>
                  <button className="plm-btn-ghost danger" onClick={() => handleDeleteTc(tc.id)}><Trash2 size={14} /></button>
                </div>
              </div>

              {expandedId === tc.id && (
                <div className="plm-card-body" style={{ paddingTop: 14, fontSize: "0.85rem" }}>
                  {loadingDetail ? (
                    <div className="plm-loading"><div className="plm-loading-dots"><span /><span /><span /></div></div>
                  ) : detail ? (
                    <>
                      {detail.description && (
                        <div className="mb-2">
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Test Steps</div>
                          <div style={{ color: "var(--plm-text-muted)", whiteSpace: "pre-line" }}>{detail.description}</div>
                        </div>
                      )}
                      {detail.preconditions && (
                        <div className="mb-2">
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Preconditions</div>
                          <div style={{ color: "var(--plm-text-muted)" }}>{detail.preconditions}</div>
                        </div>
                      )}
                      {detail.expectedResult && (
                        <div className="mb-2">
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Expected Result</div>
                          <div style={{ color: "var(--plm-text-muted)" }}>{detail.expectedResult}</div>
                        </div>
                      )}
                      {detail.tags?.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mb-3">
                          {detail.tags.map((t) => <span key={t} className="plm-tag">{t}</span>)}
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-between mb-2 mt-3"
                        style={{ borderTop: "1px solid var(--plm-border)", paddingTop: 12 }}>
                        <div style={{ fontWeight: 600 }}>Execution History</div>
                        <button className="plm-btn-ghost success" onClick={() => openExec(tc.id)}>
                          <Play size={14} /> Record
                        </button>
                      </div>
                      {executions.length === 0 ? (
                        <div style={{ color: "var(--plm-text-muted)", fontSize: "0.82rem" }}>No executions recorded.</div>
                      ) : (
                        <div>
                          {executions.map((ex) => (
                            <div key={ex.id} className="plm-exec-item">
                              <div className={`plm-exec-dot ${execDotClass(ex.status)}`} />
                              <span className={`plm-badge ${execBadgeColor(ex.status)}`}>{ex.status}</span>
                              {ex.appVersion && <span style={{ color: "var(--plm-text-muted)", fontSize: "0.78rem" }}>{ex.appVersion}</span>}
                              <span style={{ color: "var(--plm-text-muted)", fontSize: "0.75rem", marginLeft: "auto" }}>
                                {new Date(ex.executedAt).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                          {executions.some(ex => ex.actualResult || ex.errorLog) && (
                            <div style={{ marginTop: 8, fontSize: "0.8rem" }}>
                              {executions.filter(ex => ex.actualResult).slice(0, 1).map(ex => (
                                <div key={ex.id} style={{ color: "var(--plm-text-muted)", marginBottom: 4 }}>
                                  Latest: {ex.actualResult}
                                </div>
                              ))}
                              {executions.filter(ex => ex.errorLog).slice(0, 1).map(ex => (
                                <div key={ex.id} style={{ color: "var(--plm-danger)" }}>
                                  Error: {ex.errorLog}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Test Case Form */}
      <EntityFormDialog show={showTcDlg} onClose={() => setShowTcDlg(false)}
        title={editingTc ? "Edit Test Case" : "New Test Case"} onSubmit={handleTcSubmit} saving={saving}>
        <Form.Group className="mb-3">
          <Form.Label>Title *</Form.Label>
          <Form.Control required value={tcForm.title} onChange={(e) => setTc("title", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Linked Requirement</Form.Label>
          <Form.Select value={tcForm.requirementId} onChange={(e) => setTc("requirementId", e.target.value)}>
            <option value="">-- None --</option>
            {requirements.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Test Steps</Form.Label>
          <Form.Control as="textarea" rows={3} value={tcForm.description}
            onChange={(e) => setTc("description", e.target.value)} placeholder="1. Do X&#10;2. Do Y&#10;3. Verify Z" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Preconditions</Form.Label>
          <Form.Control value={tcForm.preconditions} onChange={(e) => setTc("preconditions", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Expected Result</Form.Label>
          <Form.Control value={tcForm.expectedResult} onChange={(e) => setTc("expectedResult", e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Tags</Form.Label>
          <TagInput tags={tcForm.tags} onChange={(t) => setTc("tags", t)} suggestions={tagNames} />
        </Form.Group>
      </EntityFormDialog>

      {/* Execution Form */}
      <EntityFormDialog show={showExecDlg} onClose={() => setShowExecDlg(false)}
        title="Record Test Execution" onSubmit={handleExecSubmit} saving={saving} submitLabel="Record">
        <Form.Group className="mb-3">
          <Form.Label>Status *</Form.Label>
          <Form.Select required value={execForm.status} onChange={(e) => setExec("status", e.target.value)}>
            {EXEC_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>App Version</Form.Label>
          <Form.Control placeholder="e.g. v1.0.0" value={execForm.appVersion} onChange={(e) => setExec("appVersion", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Actual Result</Form.Label>
          <Form.Control as="textarea" rows={2} value={execForm.actualResult} onChange={(e) => setExec("actualResult", e.target.value)} />
        </Form.Group>
        {execForm.status === "Failed" && (
          <Form.Group>
            <Form.Label>Error Log</Form.Label>
            <Form.Control as="textarea" rows={2} value={execForm.errorLog} onChange={(e) => setExec("errorLog", e.target.value)} />
          </Form.Group>
        )}
      </EntityFormDialog>
    </div>
  );
};

export default TestCasesTab;
